// src/app/services/seo.service.ts

import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Title, Meta } from '@angular/platform-browser';

const BASE = 'Lydia Christensen for U.S. Senate';

const BASE_URL =
  'https://www.lydialynnchristensen.com';

const LYDIA_ID =
  `${BASE_URL}/#lydia-christensen`;

const CAMPAIGN_ID =
  `${BASE_URL}/#campaign`;

const WEBSITE_ID =
  `${BASE_URL}/#website`;

const OG_IMAGE =
  `${BASE_URL}/assets/bannerFinalizedNoStars.png`;

const STRUCTURED_DATA_ID =
  'dynamic-structured-data';

const CANONICAL_ID =
  'canonical-url';

@Injectable({ providedIn: 'root' })
export class SeoService {

  constructor(
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT)
    private document: Document
  ) {}

  /**
   * Update standard SEO metadata for the current page.
   */
  update(
    pageTitle: string,
    description: string,
    path = '',
    image = ''
  ): void {

    const fullTitle =
      `${pageTitle} | ${BASE}`;

    const normalizedPath = path
      ? `/${path.replace(/^\/+|\/+$/g, '')}`
      : '/';

    const url =
      `${BASE_URL}${normalizedPath}`;

    const ogImg = image
      ? (
          image.startsWith('http')
            ? image
            : `${BASE_URL}/${image.replace(/^\/+/, '')}`
        )
      : OG_IMAGE;

    this.title.setTitle(fullTitle);

    this.meta.updateTag({
      name: 'description',
      content: description
    });

    this.meta.updateTag({
      property: 'og:title',
      content: fullTitle
    });

    this.meta.updateTag({
      property: 'og:description',
      content: description
    });

    this.meta.updateTag({
      property: 'og:url',
      content: url
    });

    this.meta.updateTag({
      property: 'og:image',
      content: ogImg
    });

    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image'
    });

    this.meta.updateTag({
      name: 'twitter:title',
      content: fullTitle
    });

    this.meta.updateTag({
      name: 'twitter:description',
      content: description
    });

    this.meta.updateTag({
      name: 'twitter:image',
      content: ogImg
    });

    this.setCanonical(url);
  }

  /**
   * Set the canonical URL for the current page.
   */
  setCanonical(url: string): void {

    let canonical =
      this.document.getElementById(
        CANONICAL_ID
      ) as HTMLLinkElement | null;

    if (!canonical) {
      canonical =
        this.document.createElement('link');

      canonical.id =
        CANONICAL_ID;

      canonical.rel =
        'canonical';

      this.document.head.appendChild(
        canonical
      );
    }

    canonical.href = url;
  }

  /**
   * Injects or replaces the page-specific
   * JSON-LD structured data.
   */
  setStructuredData(data: object): void {

    const existing =
      this.document.getElementById(
        STRUCTURED_DATA_ID
      );

    if (existing) {
      existing.remove();
    }

    const script =
      this.document.createElement('script');

    script.type =
      'application/ld+json';

    script.id =
      STRUCTURED_DATA_ID;

    script.text =
      JSON.stringify(data);

    this.document.head.appendChild(
      script
    );
  }

  /**
   * Permanent entity IDs used throughout
   * the website.
   *
   * Every page that refers to Lydia should
   * use LYDIA_ID.
   *
   * Every page that refers to the campaign
   * should use CAMPAIGN_ID.
   *
   * Every page that refers to the website
   * should use WEBSITE_ID.
   */
  get lydiaId(): string {
    return LYDIA_ID;
  }

  get campaignId(): string {
    return CAMPAIGN_ID;
  }

  get websiteId(): string {
    return WEBSITE_ID;
  }

  /**
   * Homepage SEO metadata.
   */
  setHome(): void {

    this.update(
      'Lydia Christensen',
      'Lydia Christensen is an American politician from Michigan and a Libertarian candidate for the United States Senate in 2026. Learn about her campaign, platform, and policy positions.',
      ''
    );
  }
}